import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '@/services/api';

interface WaitlistJoinButtonProps {
  branchId: string;
  /** Optional preferred table; omit for "any table". */
  tableId?: string;
  branchName?: string;
  /** Optional style override for the trigger button. */
  className?: string;
}

const todayStamp = (): string => new Date().toISOString().slice(0, 10);

/**
 * Self-contained "Join waitlist" control: a button that opens a small modal to
 * register the customer on a branch's waitlist. Reusable anywhere a branch is in
 * context (e.g. the tables screen when everything is full).
 */
const WaitlistJoinButton: React.FC<WaitlistJoinButtonProps> = ({ branchId, tableId, branchName, className }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayStamp());
  const [partySize, setPartySize] = useState('2');
  const [startTime, setStartTime] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.joinWaitlist(branchId, {
        requestedDate: date,
        tableId,
        requestedStartTime: startTime ? `${startTime}:00` : undefined,
        partySize: Number(partySize) || 1,
        notes: notes.trim() || undefined,
      });
      if (res.success) {
        setDone(true);
      } else {
        setError(res.message || 'Could not join the waitlist.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join the waitlist.');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setDone(false);
    setError(null);
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.55rem 0.65rem',
    borderRadius: 8,
    border: '1px solid var(--border, #d1d5db)',
    background: 'var(--bg-card, #fff)',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        style={
          className
            ? undefined
            : {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.9rem',
                borderRadius: 999,
                border: '1px solid var(--accent-orange, #FF6B35)',
                background: 'transparent',
                color: 'var(--accent-orange, #FF6B35)',
                fontWeight: 600,
                cursor: 'pointer',
              }
        }
      >
        🔔 Join waitlist
      </button>

      {open && createPortal(
        <div
          onClick={close}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card, #fff)',
              color: 'var(--text-primary, #1f2937)',
              borderRadius: 14,
              width: '100%',
              maxWidth: 400,
              padding: '1.25rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Join waitlist{branchName ? ` — ${branchName}` : ''}</h3>
              <button type="button" onClick={close} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'inherit' }}>×</button>
            </div>

            {done ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ fontSize: '2rem' }}>✅</div>
                <p style={{ margin: '0.5rem 0 1rem' }}>You're on the waitlist. We'll notify you the moment a table frees up.</p>
                <button type="button" onClick={close} style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', background: 'var(--accent-orange, #FF6B35)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Done</button>
              </div>
            ) : (
              <>
                <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--text-secondary, #6b7280)' }}>
                  We'll hold your place and ping you when a table opens up.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    Date
                    <input type="date" value={date} min={todayStamp()} onChange={(e) => setDate(e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    Party size
                    <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    Preferred time (optional)
                    <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={fieldStyle} />
                  </label>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    Notes (optional)
                    <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. window seat preferred" style={fieldStyle} />
                  </label>
                </div>
                {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 0 }}>{error}</p>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                  <button type="button" onClick={close} disabled={busy} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid var(--border, #d1d5db)', background: 'transparent', color: 'inherit', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button type="button" onClick={submit} disabled={busy} style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', background: 'var(--accent-orange, #FF6B35)', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy ? 'Joining…' : 'Join waitlist'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default WaitlistJoinButton;
